import { createErrorFromResponse, getContentFromResponse } from '@magicdoor/errors';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/polyfills.ts
if (Promise.withResolvers === void 0) {
  Promise.withResolvers = () => {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

// src/types.ts
var AuthMethod = /* @__PURE__ */ ((AuthMethod2) => {
  AuthMethod2["PASSWORD"] = "password";
  AuthMethod2["OTP"] = "otp";
  AuthMethod2["MAGIC_LINK"] = "magic_link";
  return AuthMethod2;
})(AuthMethod || {});
var _OAuth = class _OAuth {
  /**
   * The `MagicAuth` class constructor.
   * @param config - The authentication configuration.
   * @param eventBus - An optional event bus instance that supports `addEventListener` and `dispatchEvent`. If not provided, a default `EventTarget` will be created.
   */
  constructor(config) {
    __publicField(this, "config");
    __publicField(this, "sessionStorage");
    __publicField(this, "eventBus");
    __publicField(this, "tokenStorage");
    __publicField(this, "pkceVerifier", null);
    __publicField(this, "state", null);
    __publicField(this, "getAccessTokenPromise", null);
    __publicField(this, "authenticated");
    __publicField(this, "loggerPrefix", "MagicAuth");
    __publicField(this, "isLogingOut", false);
    var _a, _b, _c;
    this.config = config;
    this.sessionStorage = (_a = config.storage) != null ? _a : sessionStorage;
    this.tokenStorage = (_b = config.refreshTokenStorage) != null ? _b : localStorage;
    this.eventBus = (_c = config.eventBus) != null ? _c : new EventTarget();
    this.pkceVerifier = this.sessionStorage.getItem("pkce_verifier");
    this.state = this.sessionStorage.getItem("state");
  }
  generatePKCE() {
    return __async(this, null, function* () {
      const generateRandomString = (length) => {
        const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
        const array = new Uint8Array(length);
        crypto.getRandomValues(array);
        return Array.from(array).map((x) => charset[x % charset.length]).join("");
      };
      const base64URLEncode = (buffer) => {
        return btoa(String.fromCharCode(...new Uint8Array(buffer))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
      };
      const sha256 = (plain) => __async(this, null, function* () {
        const encoder = new TextEncoder();
        const data = encoder.encode(plain);
        return yield crypto.subtle.digest("SHA-256", data);
      });
      const verifier = generateRandomString(128);
      const challengeBuffer = yield sha256(verifier);
      const challenge = yield base64URLEncode(challengeBuffer);
      this.pkceVerifier = verifier;
      this.sessionStorage.setItem("pkce_verifier", verifier);
      return { verifier, challenge };
    });
  }
  parseResponse(response, parser = (response2) => response2.json()) {
    return __async(this, null, function* () {
      if (!response.ok) {
        throw createErrorFromResponse(response.status, yield getContentFromResponse(response));
      }
      if (parser) {
        return parser(response);
      }
      return response;
    });
  }
  generateState() {
    const state = Math.random().toString(36).substring(2);
    this.state = state;
    this.sessionStorage.setItem("state", state);
    return state;
  }
  refreshToken() {
    return __async(this, null, function* () {
      const refreshToken = this.getItemFromTokenStorage("refresh_token");
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }
      const response = yield fetch(`${this.config.authRoot}/oauth/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          grant_type: "refresh_token",
          client_id: this.config.clientId,
          refresh_token: refreshToken
        })
      });
      const tokens = yield this.parseResponse(response);
      this.saveTokens(tokens);
      return tokens;
    });
  }
  logout() {
    return __async(this, null, function* () {
      if (this.isLogingOut) {
        throw new Error("Already logging out");
      }
      try {
        this.isLogingOut = true;
        const refreshToken = this.getItemFromTokenStorage("refresh_token");
        if (refreshToken) {
          const authHeader = yield this.getAuthHeader();
          const response = yield fetch(`${this.config.authRoot}/oauth/revoke`, {
            method: "POST",
            headers: Object.assign({}, { "Content-Type": "application/x-www-form-urlencoded" }, authHeader),
            body: new URLSearchParams({
              token: refreshToken,
              client_id: this.config.clientId
            })
          });
          yield this.parseResponse(response, null);
        }
      } catch (error) {
      } finally {
        this.isLogingOut = false;
        this.clearTokens();
      }
    });
  }
  get storageKey() {
    return `MagicAuth:${this.config.clientId}`;
  }
  getTokenStorage() {
    const currentValueString = this.tokenStorage.getItem(this.storageKey);
    let currentValue = {};
    try {
      if (currentValueString) {
        currentValue = JSON.parse(currentValueString);
      }
    } catch (error) {
    }
    return currentValue;
  }
  saveItem2TokenStorage(key, value) {
    const currentValue = this.getTokenStorage();
    this.tokenStorage.setItem(this.storageKey, JSON.stringify(Object.assign(currentValue, { [key]: value })));
  }
  removeItemFromTokenStorage(key) {
    let currentValue = this.getTokenStorage();
    if (key) {
      delete currentValue[key];
    } else {
      currentValue = {};
    }
    this.tokenStorage.setItem(this.storageKey, JSON.stringify(currentValue));
  }
  getItemFromTokenStorage(key) {
    const currentValue = this.getTokenStorage();
    return currentValue[key] || null;
  }
  saveTokens(tokens) {
    this.authenticated = true;
    this.eventBus.dispatchEvent(new CustomEvent(_OAuth.authChangeEvent, { detail: tokens }));
    this.saveItem2TokenStorage("access_token", tokens.access_token);
    this.saveItem2TokenStorage("refresh_token", tokens.refresh_token);
    this.saveItem2TokenStorage("token_expiry", String(Date.now() + tokens.expires_in * 1e3));
    this.saveItem2TokenStorage("token_type", tokens.token_type);
  }
  clearTokens() {
    this.authenticated = false;
    this.removeItemFromTokenStorage();
  }
  getAccessToken() {
    return __async(this, null, function* () {
      const getter = () => __async(this, null, function* () {
        try {
          const token = this.getItemFromTokenStorage("access_token");
          const expiry = this.getItemFromTokenStorage("token_expiry");
          if (!token || Date.now() > Number(expiry) - 3e5) {
            return (yield this.refreshToken()).access_token;
          }
          this.eventBus.dispatchEvent(new CustomEvent(_OAuth.getAccessTokenEvent, { detail: token }));
          return token;
        } catch (error) {
          this.eventBus.dispatchEvent(new CustomEvent(_OAuth.getAccessTokenErrorEvent, { detail: error }));
          throw error;
        }
      });
      if (!this.getAccessTokenPromise) {
        this.getAccessTokenPromise = getter().finally(() => {
          this.getAccessTokenPromise = null;
        });
      }
      return this.getAccessTokenPromise;
    });
  }
  getAuthHeader() {
    return __async(this, null, function* () {
      const token = yield this.getAccessToken();
      const tokenType = this.getItemFromTokenStorage("token_type");
      if (!tokenType) {
        throw new Error("No token type available");
      }
      if (!token) {
        throw new Error("No token available");
      }
      return { Authorization: `${tokenType} ${token}` };
    });
  }
  getAccessTokenUntillAuthenticated() {
    return __async(this, arguments, function* (timeout = 5 * 60 * 1e3) {
      const { promise, resolve, reject } = Promise.withResolvers();
      if (this.authenticated) {
        resolve(yield this.getAccessToken());
      }
      const interval = setInterval(() => __async(this, null, function* () {
        if (this.authenticated) {
          clearInterval(interval);
          resolve(yield this.getAccessToken());
        }
      }), 200);
      setTimeout(() => {
        clearInterval(interval);
        reject(new Error("Timeout waiting for authentication"));
      }, timeout);
      return promise;
    });
  }
  on(event, listener) {
    this.eventBus.addEventListener(event, listener);
  }
  off(event, listener) {
    this.eventBus.removeEventListener(event, listener);
  }
  parseJWT(token) {
    try {
      const payload = token.split(".")[1];
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch (error) {
      throw new Error("Invalid JWT token");
    }
  }
};
__publicField(_OAuth, "getAccessTokenErrorEvent", "magic_auth:get_access_token_error");
__publicField(_OAuth, "getAccessTokenEvent", "magic_auth:get_access_token");
__publicField(_OAuth, "authChangeEvent", "magic_auth:auth_change");
var OAuth = _OAuth;

// src/magicAuth.ts
var MagicAuth = class extends OAuth {
  constructor(config) {
    super(config);
  }
  initiateOAuthFlow(interactionCode) {
    return __async(this, null, function* () {
      const { challenge } = yield this.generatePKCE();
      const state = this.generateState();
      const params = new URLSearchParams({
        response_type: "code",
        client_id: this.config.clientId,
        redirect_uri: this.config.callbackUrl,
        code_challenge: challenge,
        code_challenge_method: "S256",
        state,
        interaction_code: interactionCode
      });
      window.location.href = `${this.config.authRoot}/oauth/authorize?${params}`;
    });
  }
  handleCallback(code, state) {
    return __async(this, null, function* () {
      if (state !== this.state) {
        throw new Error("Invalid state parameter");
      }
      if (!this.pkceVerifier) {
        throw new Error("PKCE verifier not found");
      }
      const response = yield fetch(`${this.config.authRoot}/oauth/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          grant_type: "authorization_code",
          client_id: this.config.clientId,
          code,
          redirect_uri: this.config.callbackUrl,
          code_verifier: this.pkceVerifier
        })
      });
      const tokens = yield this.parseResponse(response);
      this.saveTokens(tokens);
      return tokens;
    });
  }
  passwordLogin(params) {
    return __async(this, null, function* () {
      const response = yield fetch(`${this.config.appRoot}/auth/password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params)
      });
      return this.parseResponse(response);
    });
  }
  sendOTP(params) {
    return __async(this, null, function* () {
      const response = yield fetch(`${this.config.appRoot}/auth/otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params)
      });
      return this.parseResponse(response);
    });
  }
  verifyOTP(params) {
    return __async(this, null, function* () {
      const response = yield fetch(`${this.config.appRoot}/auth/otp/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params)
      });
      return this.parseResponse(response);
    });
  }
  sendMagicLink(params) {
    return __async(this, null, function* () {
      const response = yield fetch(`${this.config.appRoot}/auth/magic-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params)
      });
      return this.parseResponse(response);
    });
  }
  recoverPassword(params) {
    return __async(this, null, function* () {
      const response = yield fetch(`${this.config.appRoot}/auth/recover-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params)
      });
      return this.parseResponse(response);
    });
  }
  resetPassword(params) {
    return __async(this, null, function* () {
      const response = yield fetch(`${this.config.appRoot}/auth/reset-password/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params)
      });
      yield this.parseResponse(response, null);
    });
  }
};

export { AuthMethod, MagicAuth, OAuth };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map